// Your own account: name, email (changing it needs your current password) and
// password. Phone is in the prototype but the user record has no phone field
// (redesignFlags.profilePhone).
import { useEffect, useState } from 'react';
import { authApi } from '@app/api/auth';
import { useAuth } from '@app/auth/AuthContext';
import { useGroup } from '@admin/shared/contexts/GroupContext';
import type { User } from '@app/types';
import { Avatar, Button, Card, ErrorState, Field, Input, Modal, ModalActions, SkeletonRows, useToast } from '@app/components/ui';
import { redesignFlags } from '@app/featureFlags';
import { logger } from '@app/utils/logger';

const MIN_PASSWORD = 8;

export const Profile: React.FC = () => {
  const { updateUser } = useAuth();
  const { selectedGroup } = useGroup();
  const toast = useToast();
  const [profile, setProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [errors, setErrors] = useState<{ name?: string; email?: string; password?: string }>({});
  const [saving, setSaving] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const data = await authApi.getProfile();
      setProfile(data);
      setName(data.username || '');
      setEmail(data.email || '');
    } catch (err) {
      logger.error('Failed to load profile:', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const emailChanged = !!profile && email.trim() !== profile.email;
  const dirty = !!profile && (name.trim() !== profile.username || emailChanged);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    if (!name.trim()) next.name = 'Enter your name';
    if (!email.trim()) next.email = 'Enter your email';
    if (emailChanged && !currentPassword.trim()) next.password = 'Enter your current password to change your email';
    setErrors(next);
    if (Object.keys(next).length) return;
    setSaving(true);
    try {
      const updated = await authApi.updateUserProfile({ username: name.trim(), email: email.trim() }, emailChanged ? currentPassword.trim() : undefined);
      setProfile(updated);
      updateUser(updated);
      setCurrentPassword('');
      toast('Profile saved');
    } catch (err: any) {
      toast(err?.response?.data?.message || 'Could not save your profile', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-[760px]">
        <SkeletonRows rows={3} />
      </div>
    );
  }
  if (loadError || !profile) {
    return (
      <div className="max-w-[760px]">
        <h1 className="wp-h1 mb-6">Profile</h1>
        <ErrorState title="Couldn't load your profile" body="Check your connection and try again." onRetry={load} />
      </div>
    );
  }

  return (
    <>
    <form className="max-w-[760px]" onSubmit={save} noValidate>
      <div className="flex items-center gap-[18px] mb-[26px]">
        <Avatar name={profile.username || profile.email} size={72} />
        <div className="min-w-0">
          <h1 className="wp-h1 !text-[clamp(28px,4.5vw,36px)] truncate">{profile.username || profile.email}</h1>
          <div className="text-[13.5px] text-t4 mt-1.5">
            {profile.userRole}
            {selectedGroup ? ` · ${selectedGroup.name}` : ''}
          </div>
        </div>
      </div>

      <Card className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(min(240px,100%),1fr))' }}>
        <Field label="Full name" htmlFor="pf-name" error={errors.name}>
          <Input id="pf-name" autoComplete="name" value={name} invalid={!!errors.name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Email" htmlFor="pf-email" error={errors.email}>
          <Input id="pf-email" type="email" autoComplete="email" value={email} invalid={!!errors.email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        {redesignFlags.profilePhone && (
          <Field label="Phone" htmlFor="pf-phone">
            <Input id="pf-phone" type="tel" autoComplete="tel" disabled />
          </Field>
        )}
        {emailChanged && (
          <Field label="Current password" htmlFor="pf-current" error={errors.password} hint="Needed to change your email." className="wp-fade">
            <Input
              id="pf-current"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              invalid={!!errors.password}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </Field>
        )}
      </Card>

      <Card className="flex flex-wrap items-center gap-3.5 mt-3.5">
        <div className="flex-1 min-w-[200px]">
          <div className="text-[15px] font-semibold">Password</div>
          <div className="text-[13px] text-t4 mt-[3px]">Use at least {MIN_PASSWORD} characters.</div>
        </div>
        <Button variant="ghost" onClick={() => setPwOpen(true)}>
          Change password
        </Button>
      </Card>

      <div className="flex justify-end mt-[18px]">
        <Button type="submit" size="lg" loading={saving} disabled={!dirty}>
          Save profile
        </Button>
      </div>
    </form>
    <PasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
    </>
  );
};

function PasswordModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setCurrent('');
      setNext('');
      setConfirm('');
      setError('');
    }
  }, [open]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (current.length < MIN_PASSWORD) return setError(`Your current password is at least ${MIN_PASSWORD} characters`);
    if (next.length < MIN_PASSWORD) return setError(`Use at least ${MIN_PASSWORD} characters for the new password`);
    if (next !== confirm) return setError("The new passwords don't match");
    setError('');
    setBusy(true);
    try {
      await authApi.setPassword(current, next);
      toast('Password changed');
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Could not change your password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Change password" width={440} dismissible={!busy}>
      <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
        <Field label="Current password" htmlFor="pw-current">
          <Input id="pw-current" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
        </Field>
        <Field label="New password" htmlFor="pw-new">
          <Input id="pw-new" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />
        </Field>
        <Field label="Confirm new password" htmlFor="pw-confirm" error={error}>
          <Input id="pw-confirm" type="password" autoComplete="new-password" value={confirm} invalid={!!error} onChange={(e) => setConfirm(e.target.value)} />
        </Field>
        <ModalActions onCancel={onClose} confirmLabel="Change password" busy={busy} />
      </form>
    </Modal>
  );
}
